import React, { useCallback, useEffect, useState } from 'react';
import { api } from './api.js';
import { hasBrowserWallet, isMobile, sendStablecoin, signInWithBrowserWallet, signInWithDemoWallet, walletDeepLinks } from './wallet.js';
import { CATEGORIES, allMarkets, num, usd } from './format.js';
import { Footer, Logo, MarketIcon, Modal, PageShell, Rule, SitePrefs, brand, logoText } from './ui.jsx';
import { TermsContent } from './legal.jsx';
import { t, tk, tErr, fmtDateTime, fmtDate } from './i18n.js';

const LANDING_CAT = { crypto: tk('Crypto'), metals: tk('Metals'), stocks: tk('Stocks'), indices: tk('Indices & ETFs'), commodities: tk('Commodities'), forex: tk('Forex') };

// ---------------------------------------------------------------- landing / sign-in
export function Login({ cfg, onSignedIn, toast, go }) {
  const [busy, setBusy] = useState(null);
  const go2 = async (kind) => {
    setBusy(kind);
    try {
      if (kind === 'wallet') await signInWithBrowserWallet(); else await signInWithDemoWallet();
      await onSignedIn();
    } catch (e) {
      toast(e.code === 4001 ? t('Signature request was rejected in your wallet.') : tErr(e.message), 'error');
    } finally { setBusy(null); }
  };
  const noWallet = !hasBrowserWallet();
  const mobileNoWallet = noWallet && isMobile();
  const catName = (c) => (LANDING_CAT[c.id] ? t(LANDING_CAT[c.id]) : c.label);
  const counts = CATEGORIES.map((c) => [c, allMarkets().filter((m) => m.category === c.id)]).filter(([, l]) => l.length);
  return (
    <div className="hero">
      <div className="hero-grid" aria-hidden="true" />
      <header className="hero-top">
        <a className="brand" href="#/"><Logo /><span className="brand-name">{logoText()}</span></a>
        <nav className="page-nav"><button className="nav-item" onClick={() => go('leaderboard')}>{t('Leaderboard')}</button></nav>
        <span><SitePrefs /></span>
      </header>
      <main className="hero-main">
        <p className="eyebrow">{t('Prop trading evaluation · {categories}', { categories: counts.map(([c]) => catName(c)).join(', ') })}</p>
        <h1>{t('Prove your edge.')}<br />{t('Earn your shot.')}</h1>
        <p className="lede">{t('Take a two-stage evaluation on {n} markets with live prices in a simulated account. Reach Stage 3 and you can be selected for our copy-trading program, with performance rewards of up to 80%.*', { n: allMarkets().length })}</p>
        <div className="hero-actions">
          {mobileNoWallet ? (
            <div className="wallet-links">
              <p className="hint">{t('Open {brand} in your wallet app to sign in:', { brand: brand.name })}</p>
              {walletDeepLinks().map((w) => <a key={w.name} className="btn primary lg" href={w.href}>{t('Open in {wallet}', { wallet: w.name })}</a>)}
            </div>
          ) : (
            <button className="btn primary lg" onClick={() => go2('wallet')} disabled={!!busy}>
              {busy === 'wallet' ? t('Check your wallet…') : t('Connect wallet')}
            </button>
          )}
          {cfg && cfg.allowDemoWallet && (
            <button className="btn ghost lg" onClick={() => go2('demo')} disabled={!!busy}>
              {busy === 'demo' ? t('Signing in…') : t('Try with a demo wallet')}
            </button>
          )}
        </div>
        {noWallet && !mobileNoWallet && <p className="hint">{t('No browser wallet detected. Install MetaMask, or use the demo wallet to look around.')}</p>}
        <dl className="hero-stats">
          <div><dt>{t('Simulated accounts up to')}</dt><dd>$50,000</dd></div>
          <div><dt>{t('Rewards up to*')}</dt><dd>80%</dd></div>
          <div><dt>{t('Daily loss limit')}</dt><dd>5%</dd></div>
          <div><dt>{t('Max drawdown')}</dt><dd>10%</dd></div>
        </dl>
        <p className="hint">{t('*All trading is simulated. Selection for copy trading and any reward are at the sole discretion of {operator} and are not guaranteed.', { operator: brand.operator })} <a href="#/terms">{t('Terms & Risk Disclosure')}</a></p>
        <div className="market-strip" aria-label={t('Markets')}>
          {counts.map(([c, list]) => (
            <div key={c.id} className="ms-group">
              <span className="ms-icons">{list.slice(0, 5).map((m) => <MarketIcon key={m.symbol} symbol={m.symbol} size={22} />)}</span>
              <span><b>{list.length}</b> {catName(c)}</span>
            </div>
          ))}
        </div>
      </main>
      <Footer />
    </div>
  );
}

// ---------------------------------------------------------------- purchase
export function BuyChallenge({ cfg, canCancel, onCancel, onBought, toast, user, signOut, go }) {
  const [tpls, setTpls] = useState(null);
  const [busy, setBusy] = useState(null);
  const [code, setCode] = useState('');
  const [applied, setApplied] = useState(null); // { code, percentOff }
  const [checkout, setCheckout] = useState(null);
  const onchain = cfg && cfg.payments && cfg.payments.mode === 'onchain';
  useEffect(() => { api('GET', '/api/challenges').then(setTpls).catch((e) => toast(tErr(e.message), 'error')); }, [toast]);
  const priceOf = (tpl) => (applied ? Math.max(0, Math.round(tpl.priceUsd * (1 - applied.percentOff / 100) * 100) / 100) : tpl.priceUsd);

  const apply = async () => {
    if (!code.trim() || !tpls || !tpls.length) return;
    try {
      const q = await api('POST', '/api/quote', { templateId: tpls[0].id, code });
      setApplied({ code: q.code, percentOff: q.percentOff });
      toast(t('Code {code} applied: {pct}% off', { code: q.code, pct: num(q.percentOff, 0) }), 'success');
    } catch (e) { setApplied(null); toast(tErr(e.message), 'error'); }
  };
  const start = async (tpl) => {
    if (onchain && priceOf(tpl) > 0) { setCheckout(tpl); return; }
    setBusy(tpl.id);
    try { onBought(await api('POST', '/api/accounts', { templateId: tpl.id, code: applied ? applied.code : undefined })); }
    catch (e) { toast(tErr(e.message), 'error'); }
    finally { setBusy(null); }
  };
  return (
    <PageShell user={user} signOut={signOut} go={go} current="buy" wide>
      <div className="center-col">
        {canCancel && <button className="btn ghost back" onClick={onCancel}>{t('← Back to terminal')}</button>}
        <p className="eyebrow">{t('Choose your challenge')}</p>
        <h1 className="h2">{t('Start your evaluation')}</h1>
      </div>
      <div className="plans">
        {!tpls ? <div className="spinner" /> : tpls.map((tpl, i) => {
          const featured = tpls.length === 3 && i === 1;
          const p = priceOf(tpl);
          return (
            <article key={tpl.id} className={'plan' + (featured ? ' featured' : '')}>
              {featured && <div className="plan-flag">{t('Most popular')}</div>}
              <div className="plan-head">
                <div>
                  <div className="plan-size">${num(tpl.startingBalance, 0)}</div>
                  <div className="muted">{t('Simulated account')}</div>
                </div>
                <div className="plan-price">
                  {applied && p !== tpl.priceUsd && <s className="was">${num(tpl.priceUsd, 0)}</s>}
                  ${num(p, p % 1 ? 2 : 0)}<span> {t('evaluation fee')}</span>
                </div>
              </div>
              <div className="plan-rules">
                <Rule label={t('Stage 1 profit target')} value={`${num(tpl.stage1ProfitTarget * 100, 0)}%`} />
                <Rule label={t('Stage 2 profit target')} value={`${num(tpl.stage2ProfitTarget * 100, 0)}%`} />
                <Rule label={t('Max daily loss')} value={`${num(tpl.maxDailyLoss * 100, 0)}%`} />
                <Rule label={t('Max total drawdown')} value={`${num(tpl.maxTotalDrawdown * 100, 0)}%`} />
                <Rule label={t('Minimum trading days')} value={tpl.minTradingDays} />
                <Rule label={t('Stage 3 reward rate*')} value={t('up to {pct}%', { pct: num((tpl.profitSplit || 0.8) * 100, 0) })} />
                <Rule label={t('Markets')} value={`${allMarkets().length}`} />
              </div>
              <button className={'btn lg block ' + (featured ? 'primary' : 'outline')} onClick={() => start(tpl)} disabled={!!busy}>
                {busy === tpl.id ? t('Starting…') : p === 0 ? t('Start free') : t('Start · ${price}', { price: num(p, p % 1 ? 2 : 0) })}
              </button>
            </article>
          );
        })}
      </div>
      <div className="coupon">
        <label htmlFor="coupon">{t('Discount code')}</label>
        <div className="coupon-row">
          <input id="coupon" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder={t('ENTER CODE')} onKeyDown={(e) => e.key === 'Enter' && apply()} />
          <button className="btn" onClick={apply}>{t('Apply')}</button>
        </div>
        {applied && <span className="up small">{t('{code}: {pct}% off applied', { code: applied.code, pct: num(applied.percentOff, 0) })}</span>}
      </div>
      <p className="hint center">{onchain ? t('Pay with USDT or USDC from your wallet. Your account opens as soon as the payment confirms.') : t('Demo mode: no payment is taken.')}</p>
      <p className="fine-center">{t('The fee buys access to an evaluation service. It is not a deposit or investment. All trading is simulated and virtual balances have no cash value.')}
        {' '}{t('*Copy-trading selection and rewards are at the sole discretion of {operator}.', { operator: brand.operator })} <a href="#/terms">{t('Terms & Risk Disclosure')}</a></p>
      {checkout && <PayModal cfg={cfg} template={checkout} code={applied ? applied.code : undefined} price={priceOf(checkout)} toast={toast}
        onClose={() => setCheckout(null)} onPaid={(a) => { setCheckout(null); onBought(a); }} />}
    </PageShell>
  );
}

function PayModal({ cfg, template, code, price: p, toast, onClose, onPaid }) {
  const chains = cfg.payments.chains;
  const [chainKey, setChainKey] = useState(chains[0] && chains[0].key);
  const chain = chains.find((c) => c.key === chainKey);
  const [token, setToken] = useState(chain && chain.tokens[0].symbol);
  const [stage, setStage] = useState('choose'); // choose | wallet | confirming | failed
  const [payment, setPayment] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => { if (chain && !chain.tokens.some((tok) => tok.symbol === token)) setToken(chain.tokens[0].symbol); }, [chainKey]); // eslint-disable-line

  const pay = async () => {
    setError(null);
    try {
      setStage('wallet');
      const pm = await api('POST', '/api/payments', { templateId: template.id, code, chain: chainKey, token });
      setPayment(pm);
      const hash = await sendStablecoin({ chain, tokenAddress: pm.tokenAddress, treasury: pm.treasury, amountUnits: pm.amountUnits });
      setStage('confirming');
      let res = await api('POST', `/api/payments/${pm.id}/submit`, { txHash: hash });
      for (let i = 0; i < 120 && res.status === 'submitted'; i++) {
        await new Promise((r) => setTimeout(r, 5000));
        res = await api('GET', `/api/payments/${pm.id}`);
      }
      setPayment(res);
      if (res.status === 'confirmed') {
        const acct = await api('GET', `/api/accounts/${res.accountId}`);
        toast(t('Payment confirmed. Your account is ready.'), 'success');
        onPaid(acct);
      } else { setStage('failed'); setError(res.error ? tErr(res.error) : t('Payment {status}', { status: res.status })); }
    } catch (e) {
      setStage('failed');
      setError(e.code === 4001 ? t('You rejected the transaction in your wallet.') : tErr(e.message));
    }
  };

  return (
    <Modal title={t('Pay for the ${amount} challenge', { amount: num(template.startingBalance, 0) })} onClose={stage === 'confirming' ? () => {} : onClose}>
      <div className="modal-body">
        <div className="pay-amount"><span className="mono">{usd(p)}</span><span className="label">{t('in {token} on {network}', { token, network: chain && chain.name })}</span></div>
        {(stage === 'choose' || stage === 'failed') && (
          <>
            <div className="field">
              <label>{t('Network')}</label>
              <div className="pill-row">{chains.map((c) => <button key={c.key} type="button" className={'pill' + (c.key === chainKey ? ' on' : '')} onClick={() => setChainKey(c.key)}>{c.name}</button>)}</div>
            </div>
            <div className="field">
              <label>{t('Token')}</label>
              <div className="pill-row">{chain && chain.tokens.map((tok) => <button key={tok.symbol} type="button" className={'pill' + (tok.symbol === token ? ' on' : '')} onClick={() => setToken(tok.symbol)}>{tok.symbol}</button>)}</div>
            </div>
            {error && <div className="risk danger"><span>{error}</span></div>}
            <button className="btn primary lg block" onClick={pay}>{t('Pay {amount} with my wallet', { amount: usd(p) })}</button>
            <p className="muted small-p">{t('Your wallet will ask you to switch to {network} and approve a {token} transfer to {operator}. You also need a little {native} for the network fee.', { network: chain && chain.name, token, operator: brand.operator, native: chain && chain.native })}</p>
          </>
        )}
        {stage === 'wallet' && <div className="pay-wait"><div className="spinner" /><p>{t('Approve the transfer in your wallet…')}</p></div>}
        {stage === 'confirming' && (
          <div className="pay-wait"><div className="spinner" /><p>{t('Waiting for the blockchain to confirm your payment. This usually takes under a minute.')}</p>
            {payment && payment.explorer && <a href={payment.explorer} target="_blank" rel="noreferrer">{t('View transaction')}</a>}</div>
        )}
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------- affiliates
// Referral progress labels arrive from the server in English.
const PROGRESS_LABEL = {
  'No challenge yet': tk('No challenge yet'),
  'Stage 1': tk('Stage 1'),
  'Stage 2': tk('Stage 2'),
  'Stage 3': tk('Stage 3'),
};
const COMMISSION_STATUS = { pending: tk('under review'), paid: tk('paid'), void: tk('void') };

export function Affiliates({ user, signOut, toast, go }) {
  const [d, setD] = useState(null);
  const load = useCallback(() => api('GET', '/api/affiliate').then(setD).catch((e) => toast(tErr(e.message), 'error')), [toast]);
  useEffect(() => { load(); const timer = setInterval(load, 10000); return () => clearInterval(timer); }, [load]);
  const link = d ? `${location.origin}/?ref=${d.code}` : '';
  const copy = async () => {
    try { await navigator.clipboard.writeText(link); toast(t('Referral link copied'), 'success'); }
    catch { toast(t('Copy failed: select the link and copy it manually'), 'error'); }
  };
  const ratePct = d ? Math.round(d.rate * 100) : 20;
  const STATUS = { real: [t('Real customer'), 'active'], demo: [t('Trying the demo'), 'demo'], signed_up: [t('Signed up'), ''] };
  return (
    <PageShell user={user} signOut={signOut} go={go} current="affiliates">
      <p className="eyebrow">{t('Affiliate program')}</p>
      <h1 className="h2 left">{t('Earn {pct}% on every real purchase you refer', { pct: ratePct })}</h1>
      <p className="lede left">{t('Share your link. You\'ll see everyone who signs up with it and how far they get. When one of them pays for an evaluation with real money, you earn {pct}%, on every real purchase they make.', { pct: ratePct })}</p>
      {!d ? <div className="spinner" /> : (
        <>
          <section className="aff-link" aria-label={t('Your referral link')}>
            <div className="label">{t('Your referral link')}</div>
            <div className="aff-link-row">
              <input readOnly value={link} onFocus={(e) => e.target.select()} aria-label={t('Referral link')} className="mono" />
              <button className="btn primary" onClick={copy}>{t('Copy link')}</button>
            </div>
            <div className="label">{t('Code')} <span className="mono">{d.code}</span></div>
          </section>
          <section className="aff-funnel" aria-label={t('Your referral funnel')}>
            <Funnel n={d.stats.clicks} label={t('Link clicks')} />
            <Funnel n={d.stats.signups} label={t('Signed up with your code')} />
            <Funnel n={d.stats.demoTraders} label={t('Trying the demo')} sub={t('No money paid, no commission')} tone="demo" />
            <Funnel n={d.stats.realCustomers} label={t('Real customers')} sub={t('{amount} in real purchases', { amount: usd(d.stats.sales) })} tone="real" />
          </section>
          <section className="aff-stats three">
            <AffStat label={t('Commission earned')} value={usd(d.stats.earned)} accent />
            <AffStat label={t('Waiting for payout')} value={usd(d.stats.pending)} />
            <AffStat label={t('Paid to you')} value={usd(d.stats.paid)} />
          </section>
          <div className="info-note">
            <b>{t('Real vs demo.')}</b> {t('Sign-ups that only use the demo, or a free code, show up here so you can follow them, but they don\'t earn commission. Commission is earned only when a trader you referred pays with real money. Commissions are reviewed before payout.')}
          </div>
          <section className="card-table">
            <h2>{t('Traders who used your code')}</h2>
            {d.referrals.length === 0 ? <div className="empty">{t('Nobody has signed up with your link yet.')}</div> : (
              <div className="table-wrap"><table>
                <thead><tr><th>{t('Trader')}</th><th>{t('Joined')}</th><th>{t('Status')}</th><th>{t('Progress')}</th><th>{t('Real purchases')}</th><th>{t('Demo accounts')}</th><th>{t('Your commission')}</th></tr></thead>
                <tbody>
                  {d.referrals.map((r, i) => (
                    <tr key={i}>
                      <td data-label={t('Trader')} className="mono">{r.wallet}</td>
                      <td data-label={t('Joined')} className="muted">{fmtDate(new Date(r.joinedAt))}</td>
                      <td data-label={t('Status')}><span className={'badge ' + STATUS[r.status][1]}>{STATUS[r.status][0]}</span></td>
                      <td data-label={t('Progress')}>{PROGRESS_LABEL[r.progress] ? t(PROGRESS_LABEL[r.progress]) : r.progress}</td>
                      <td data-label={t('Real purchases')} className="mono">{r.realPurchases ? `${r.realPurchases} · ${usd(r.realSales)}` : '—'}</td>
                      <td data-label={t('Demo accounts')} className="mono muted">{r.demoPurchases || '—'}</td>
                      <td data-label={t('Commission')} className={'mono ' + (r.commission > 0 ? 'up' : 'muted')}>{r.commission > 0 ? usd(r.commission) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table></div>
            )}
          </section>
          <section className="card-table">
            <h2>{t('Commissions (real purchases only)')}</h2>
            {d.commissions.length === 0 ? <div className="empty">{t('No real purchases yet. Commissions appear here when a trader you referred pays for an evaluation.')}</div> : (
              <div className="table-wrap"><table>
                <thead><tr><th>{t('Date')}</th><th>{t('Trader')}</th><th>{t('Challenge')}</th><th>{t('Paid by trader')}</th><th>{t('Your commission')}</th><th>{t('Status')}</th></tr></thead>
                <tbody>
                  {d.commissions.map((c) => (
                    <tr key={c.id}>
                      <td data-label={t('Date')} className="muted">{fmtDate(new Date(c.createdAt))}</td>
                      <td data-label={t('Trader')} className="mono">{c.wallet}</td>
                      <td data-label={t('Challenge')}>{c.challenge}</td>
                      <td data-label={t('Paid')} className="mono">{usd(c.purchaseAmount)}</td>
                      <td data-label={t('Commission')} className="mono up">{usd(c.amount)}</td>
                      <td data-label={t('Status')}><span className={'badge ' + (c.status === 'paid' ? 'active' : c.status === 'void' ? 'breached' : '')}>{COMMISSION_STATUS[c.status] ? t(COMMISSION_STATUS[c.status]) : c.status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table></div>
            )}
          </section>
          <p className="fine-left">{t('Commissions are earned only on evaluation fees actually received. They can be cancelled for refunds, self-referrals or abuse.')} <a href="#/terms#s10">{t('See section 10 of the Terms.')}</a></p>
        </>
      )}
    </PageShell>
  );
}
const AffStat = ({ label, value, sub, accent }) => (
  <div className={'aff-stat' + (accent ? ' accent' : '')}><span className="label">{label}</span><span className="mono v">{value}</span>{sub && <span className="label">{sub}</span>}</div>
);
const Funnel = ({ n, label, sub, tone }) => (
  <div className={'funnel-step ' + (tone || '')}><span className="mono v">{num(n, 0)}</span><span className="label strong">{label}</span>{sub && <span className="label">{sub}</span>}</div>
);

// ---------------------------------------------------------------- terms (public)
export function TermsPage({ user, signOut, go }) {
  useEffect(() => {
    const id = (location.hash.split('#')[2] || '');
    if (id) setTimeout(() => { const el = document.getElementById(id); if (el) el.scrollIntoView(); }, 50);
  }, []);
  return (
    <PageShell user={user} signOut={signOut} go={go} current="terms">
      <p className="eyebrow">{t('Legal')}</p>
      <h1 className="h2 left">{t('Terms & Risk Disclosure')}</h1>
      <TermsContent />
    </PageShell>
  );
}

// ---------------------------------------------------------------- leaderboard (public)
const STAGE = { stage_1: tk('Stage 1'), stage_2: tk('Stage 2'), funded: tk('Stage 3') };

export function Leaderboard({ user, signOut, go }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    const load = () => api('GET', '/api/leaderboard').then(setRows).catch(() => setRows([]));
    load(); const timer = setInterval(load, 15000); return () => clearInterval(timer);
  }, []);
  return (
    <PageShell user={user} signOut={signOut} go={go} current="leaderboard">
      <p className="eyebrow">{t('Leaderboard')}</p>
      <h1 className="h2 left">{t('Top traders right now')}</h1>
      <p className="lede left">{t('Ranked by closed profit on active accounts. Wallets are shortened for privacy.')}</p>
      <section className="card-table">
        {!rows ? <div className="spinner" /> : rows.length === 0 ? <div className="empty">{t('No profitable accounts yet. Be the first.')}</div> : (
          <div className="table-wrap"><table className="lb">
            <thead><tr><th>#</th><th>{t('Trader')}</th><th>{t('Account')}</th><th>{t('Stage')}</th><th>{t('Profit')}</th><th>{t('Return')}</th><th>{t('Trading days')}</th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.rank} className={r.rank <= 3 ? 'top' : ''}>
                  <td data-label="#" className="mono rank">{r.rank}</td>
                  <td data-label={t('Trader')} className="mono">{r.trader}</td>
                  <td data-label={t('Account')} className="mono">${num(r.accountSize, 0)}</td>
                  <td data-label={t('Stage')}><span className={'badge ' + (r.stage === 'funded' ? 'active' : '')}>{STAGE[r.stage] ? t(STAGE[r.stage]) : r.stage}</span></td>
                  <td data-label={t('Profit')} className="mono up">{usd(r.profit)}</td>
                  <td data-label={t('Return')} className="mono up b">+{num(r.profitPct, 2)}%</td>
                  <td data-label={t('Days')} className="mono">{r.tradingDays}</td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
      </section>
    </PageShell>
  );
}

// ---------------------------------------------------------------- API keys
export function ApiKeys({ user, signOut, toast, go }) {
  const [keys, setKeys] = useState(null);
  const [name, setName] = useState('');
  const [fresh, setFresh] = useState(null);
  const load = useCallback(() => api('GET', '/api/keys').then(setKeys).catch((e) => toast(tErr(e.message), 'error')), [toast]);
  useEffect(() => { load(); }, [load]);
  const create = async () => {
    try { const k = await api('POST', '/api/keys', { name: name || t('API key') }); setFresh(k); setName(''); load(); }
    catch (e) { toast(tErr(e.message), 'error'); }
  };
  const revoke = async (id) => {
    try { await api('DELETE', `/api/keys/${id}`); toast(t('Key revoked'), 'info'); load(); }
    catch (e) { toast(tErr(e.message), 'error'); }
  };
  const copy = async (text) => { try { await navigator.clipboard.writeText(text); toast(t('Copied'), 'success'); } catch { /* ignore */ } };
  return (
    <PageShell user={user} signOut={signOut} go={go} current="api">
      <p className="eyebrow">{t('Developers')}</p>
      <h1 className="h2 left">{t('API keys & SDK')}</h1>
      <p className="lede left">{t('Trade from your own scripts and bots. Keys can read data and place orders; payouts and new keys always need your wallet.')}</p>
      <section className="aff-link">
        <div className="label">{t('Create a key')}</div>
        <div className="aff-link-row">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder={t('Name, e.g. My trading bot')} aria-label={t('Key name')} />
          <button className="btn primary" onClick={create}>{t('Create key')}</button>
        </div>
        {fresh && (
          <div className="fresh-key">
            <div className="label">{t('Copy this key now. It won\'t be shown again.')}</div>
            <div className="aff-link-row"><input readOnly className="mono" value={fresh.key} onFocus={(e) => e.target.select()} aria-label={t('New API key')} /><button className="btn" onClick={() => copy(fresh.key)}>{t('Copy')}</button></div>
          </div>
        )}
      </section>
      <section className="card-table">
        <h2>{t('Your keys')}</h2>
        {!keys ? <div className="spinner" /> : keys.length === 0 ? <div className="empty">{t('No API keys yet.')}</div> : (
          <div className="table-wrap"><table>
            <thead><tr><th>{t('Name')}</th><th>{t('Key')}</th><th>{t('Created')}</th><th>{t('Last used')}</th><th className="r">{t('Action')}</th></tr></thead>
            <tbody>
              {keys.map((k) => (
                <tr key={k.id}>
                  <td data-label={t('Name')}>{k.name}</td>
                  <td data-label={t('Key')} className="mono">{k.prefix}…</td>
                  <td data-label={t('Created')} className="muted">{fmtDate(new Date(k.created_at))}</td>
                  <td data-label={t('Last used')} className="muted">{k.last_used_at ? fmtDateTime(new Date(k.last_used_at)) : t('Never')}</td>
                  <td className="r"><button className="btn sm" onClick={() => revoke(k.id)}>{t('Revoke')}</button></td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
      </section>
      <section className="card-table docs">
        <h2>{t('Quick start')}</h2>
        <pre className="code">{`import { Fundex } from './fundex.js';   // sdk/js in the project

const mt = new Fundex({ baseUrl: '${location.origin}', apiKey: 'mt_...' });
const { accounts } = await mt.me();
await mt.placeOrder(accounts[0].id, {
  symbol: 'BTCUSDT', side: 'buy', qty: 0.01, leverage: 5, slPrice: 60000,
});
mt.stream({ onMarket: (m) => console.log(m.prices.BTCUSDT.price) });`}</pre>
        <p className="muted small-p">{t('Full reference:')} <a href="/api/v1/openapi.json" target="_blank" rel="noreferrer">/api/v1/openapi.json</a> {t('(OpenAPI 3.1).')} {t('Every endpoint is under {path}; live data on {ws}.', { path: '/api/v1', ws: '/api/v1/ws' })}</p>
      </section>
    </PageShell>
  );
}
